export const bearclawPosts = [
  {
    slug: 'what-is-a-personal-ai-workspace',
    category: 'guides',
    categoryLabel: 'Guide',
    date: 'Apr 3, 2026',
    coverImage: '/bearclaw-cover-guide.svg',
    title: 'What a Personal AI Workspace Actually Is',
    summary:
      'BearClaw keeps your files, tasks, and conversations in one place so you can pick up work without starting over every time.',
    featured: true,
    readTime: '4 min read',
    contentHtml: `
      <h2>More than a chat box</h2>
      <p>Most AI tools start fresh every time you open them. BearClaw is different. It gives you a persistent workspace where your files, running tasks, and conversation context stay together.</p>
      <p>That means you can upload documents, connect tools, and come back later without rebuilding the same context from scratch.</p>

      <h2>Why that matters</h2>
      <p>People do not work in isolated prompts. Real work spans files, decisions, follow-ups, and repeated tasks. BearClaw is designed to keep that continuity intact.</p>
      <ul>
        <li>Your files stay available in the same workspace</li>
        <li>Your conversations keep context across sessions</li>
        <li>Your connected tools stay tied to the work you are doing</li>
      </ul>

      <h2>Built on real platform capabilities</h2>
      <p>Under the hood, BearClaw uses BlueBear workspaces, persistent sessions, and secure execution environments. On the surface, that translates into a product that feels simple: one place to work with your AI.</p>
    `,
  },
  {
    slug: 'how-bearclaw-works-with-files-and-documents',
    category: 'workflows',
    categoryLabel: 'Workflow',
    date: 'Apr 3, 2026',
    coverImage: '/bearclaw-cover-workflow.svg',
    title: 'How BearClaw Works with Files and Documents',
    summary:
      'Upload files, summarize long documents, and keep source material inside the same workspace instead of scattering work across tabs.',
    featured: true,
    readTime: '5 min read',
    contentHtml: `
      <h2>Start with the file, not the prompt</h2>
      <p>BearClaw is designed for real inputs. You can upload documents, keep them in your workspace, and ask questions against the material without manually copying everything into chat.</p>

      <h2>Useful everyday workflows</h2>
      <ul>
        <li>Summarize a long PDF into the key takeaways</li>
        <li>Extract action items from notes or meeting transcripts</li>
        <li>Compare multiple documents in one workspace</li>
        <li>Reuse the same source material across follow-up tasks</li>
      </ul>

      <h2>Why this feels different</h2>
      <p>Most assistants treat documents like one-off uploads. BearClaw treats them like working material. They stay attached to the workspace so you can build on the same context over time.</p>
    `,
  },
  {
    slug: 'connected-apps-in-bearclaw',
    category: 'connected-apps',
    categoryLabel: 'Connected Apps',
    date: 'Apr 3, 2026',
    coverImage: '/bearclaw-cover-connected-apps.svg',
    title: 'How Connected Apps Work in BearClaw',
    summary:
      'BearClaw is built to work with connected tools so your AI can operate on real information, not isolated text prompts.',
    featured: true,
    readTime: '4 min read',
    contentHtml: `
      <h2>Connected tools make the assistant useful</h2>
      <p>BearClaw is not meant to live in isolation. It is designed to work with files, communication tools, and connected data sources so answers can be based on real inputs.</p>

      <h2>Examples of what this unlocks</h2>
      <ul>
        <li>Search files across connected storage</li>
        <li>Pull context from messages and shared documents</li>
        <li>Keep work grounded in the tools you already use</li>
      </ul>

      <h2>Control matters</h2>
      <p>Connected access should be explicit. BearClaw inherits BlueBear's model for scoped tool access and isolated workspaces, which lets the product stay useful without pretending everything is open by default.</p>
    `,
  },
  {
    slug: 'why-bearclaw-uses-isolated-workspaces',
    category: 'security',
    categoryLabel: 'Security',
    date: 'Apr 3, 2026',
    coverImage: '/bearclaw-cover-security.svg',
    title: 'Why BearClaw Uses Isolated Workspaces',
    summary:
      'Security should not be a hidden afterthought. BearClaw uses isolated workspaces so your files and tasks stay separated from everyone else.',
    featured: false,
    readTime: '4 min read',
    contentHtml: `
      <h2>Security has to be part of the product</h2>
      <p>BearClaw is built on isolated workspaces. In plain terms, that means your environment is separate, your files stay tied to your workspace, and the system is designed around separation instead of shared state.</p>

      <h2>What that means for you</h2>
      <ul>
        <li>Your work persists in your own workspace</li>
        <li>Sessions can restart without losing the workspace itself</li>
        <li>Connected tools and files stay scoped to the environment you are using</li>
      </ul>

      <h2>Simple promise, serious foundation</h2>
      <p>BearClaw should feel simple on the surface. The reason it can do that is because BlueBear already handles the harder platform pieces underneath.</p>
    `,
  },
  {
    slug: 'bearclaw-update-persistent-work',
    category: 'updates',
    categoryLabel: 'Product Update',
    date: 'Apr 3, 2026',
    coverImage: '/bearclaw-cover-update.svg',
    title: 'BearClaw Update: Better Persistent Work Across Sessions',
    summary:
      'This update sharpens the core BearClaw experience by keeping ongoing work easier to resume across conversations and workspace sessions.',
    featured: false,
    readTime: '3 min read',
    contentHtml: `
      <h2>What changed</h2>
      <p>This update focuses on continuity. BearClaw is getting tighter around the idea that work should stay in place, not disappear between sessions.</p>

      <h2>What that improves</h2>
      <ul>
        <li>Easier continuation of ongoing tasks</li>
        <li>Less repeated setup when returning to a workspace</li>
        <li>Cleaner flow between files, context, and follow-up actions</li>
      </ul>

      <h2>Why this matters</h2>
      <p>The whole value of a personal AI workspace is continuity. If users still have to restate everything on every visit, the product collapses back into a basic chat app. This update moves BearClaw further away from that pattern.</p>
    `,
  },
];

export const bearclawPostMap = bearclawPosts.reduce((acc, post) => {
  acc[post.slug] = post;
  return acc;
}, {});
